import { MedicineTimingRuleType, PillboxItem } from "@/types/pillbox";
import { MemberInfo } from "@/types/member";

// "you"/"your" when viewing your own Pillbox, nickname (or first name)
// otherwise — e.g. "5 new medications found for you." vs "...for Mom."
export function getMemberLabel(
  memberInfo: Pick<MemberInfo, "onBehalf" | "nickname" | "firstName">,
  form: "for" | "possessive" = "for"
): string {
  if (!memberInfo.onBehalf) {
    return form === "possessive" ? "your" : "you";
  }
  const name = memberInfo.nickname || memberInfo.firstName;
  return form === "possessive" ? `${name}'s` : name;
}

export const DAY_FREQUENCY_LABELS: Record<string, string> = {
  ONCE: "Once a day",
  TWICE: "Twice a day",
  THRICE: "3 times a day",
  FOUR_TIMES: "4 times a day",
};

// "Amlodipine" + "5mg" -> "Amlodipine 5mg" (dose baked into the display
// title, matching the Figma card design — name/dose stay separate fields
// in the ledger).
export function formatMedicineTitle(
  item: Pick<PillboxItem, "name" | "dose">
): string {
  return item.dose ? `${item.name} ${item.dose}` : item.name;
}

// "Every day • Once a day" / "Every 5 days • Twice a day" / "As needed"
export function formatFrequencyLine(
  item: Pick<PillboxItem, "timingRuleType" | "dayFrequency" | "frequency">,
  recurrenceIntervalDays?: number
): string {
  if (item.timingRuleType === MedicineTimingRuleType.CONDITIONAL) {
    return "As needed";
  }
  const cadence =
    !recurrenceIntervalDays || recurrenceIntervalDays <= 1
      ? "Every day"
      : `Every ${recurrenceIntervalDays} days`;
  const dayFreqLabel = item.dayFrequency
    ? DAY_FREQUENCY_LABELS[item.dayFrequency]
    : undefined;
  return dayFreqLabel ? `${cadence} • ${dayFreqLabel}` : cadence;
}

// Reverse of the fixed morning/afternoon/evening/night slot times — buckets
// an arbitrary "HH:mm" (e.g. from OCR-derived items) back to a daytime card.
export function deriveSlotFromTime(
  time: string
): "morning" | "afternoon" | "evening" | "night" {
  const hour = parseInt(time.split(":")[0], 10);
  if (hour < 12) return "morning";
  if (hour < 16) return "afternoon";
  if (hour < 20) return "evening";
  return "night";
}

const WEEKDAY_SHORT_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// "Every other day" / "Mon, Wed, Fri" / "As needed" — cadence text for the
// Reminder detail row, which (unlike formatFrequencyLine) needs to surface
// selectedWeekdays when that's how the recurrence was configured.
export function formatCadenceLine(
  item: Pick<
    PillboxItem,
    "timingRuleType" | "recurrenceIntervalDays" | "selectedWeekdays"
  >
): string {
  if (item.timingRuleType === MedicineTimingRuleType.CONDITIONAL) {
    return "As needed";
  }
  if (item.selectedWeekdays && item.selectedWeekdays.length > 0) {
    return item.selectedWeekdays.map((d) => WEEKDAY_SHORT_LABELS[d]).join(", ");
  }
  const interval = item.recurrenceIntervalDays ?? 1;
  if (interval <= 1) return "Every day";
  if (interval === 2) return "Every other day";
  return `Every ${interval} days`;
}

function formatTime12h(time: string): string {
  const [hStr, mStr] = time.split(":");
  const hour = parseInt(hStr, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${mStr}${period}`;
}

// "10:00AM • 8:00PM • 10:00PM"
export function formatSlotTimes(slots?: string[]): string {
  if (!slots || slots.length === 0) return "";
  return slots.map(formatTime12h).join(" • ");
}

// "15 Jun 2026" — startDate/endDate/dateOfVisit are stored as UTC midnight,
// so format in UTC to avoid a local-timezone day shift.
export function formatDisplayDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

// "12 May" — same UTC-safe formatting as formatDisplayDate, without the
// year, for the compact doctor+date badges on prescription review cards.
export function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

// "Dr. Mehta • 12 May" — gracefully drops whichever segment (doctor name /
// visit date) is missing, instead of rendering a blank/dangling-separator
// badge. Falls back to a generic label when OCR couldn't read either (e.g.
// an illegible prescription header).
export function formatDoctorDateBadge(
  doctorName: string | undefined,
  formattedDate: string | undefined,
  fallback: string
): string {
  const parts = [doctorName, formattedDate].filter(Boolean);
  return parts.length > 0 ? parts.join(" • ") : fallback;
}
